'use client';

import { useMemo, useState } from 'react';
import { VStack } from '@terra/ui-styled-system';
import {
  CrawlerConfigurationCard,
  ExecutionsConfigurationCard,
  NextPentestReportCard,
  ScheduledReportsConfigurationCard,
  SettingsEmptyState,
  Tabs,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { usePersistApplicationSettings } from '../../hooks/api/usePersistApplicationSettings';
import { AdaptiveScanningCard } from './AdaptiveScanningCard';
import { LoginBehaviorCard } from './LoginBehaviorCard';
import { RequestRateLimitCard } from './RequestRateLimitCard';
import { DynamicApplicationScanConfigurationCard } from './dynamic-application-scan/DynamicApplicationScanConfigurationCard';
import { DiscoveryContentTypesSection } from './DiscoveryContentTypesSection';
import { FullRescanControl } from './automation/FullRescanControl';
import { FullRescanStatusCard } from './automation/FullRescanStatusCard';
import {
  SettingsSubTabs,
  type SettingsSubTabItem,
} from './layout/SettingsSubTabs';
import { useAutomationSettings } from './automation/useAutomationSettings';
import { useDynamicApplicationScanSettings } from './dynamic-application-scan/useDynamicApplicationScanSettings';

type AutomationSubTab =
  | 'reports'
  | 'executions'
  | 'dynamicApplicationScan'
  | 'crawler';

type AutomationTabProps = {
  isReadOnly?: boolean;
  isAiApplicationTarget?: boolean;
};

const resolveVisibleSubTab = ({
  activeSubTab,
  subTabs,
}: {
  activeSubTab: AutomationSubTab;
  subTabs: ReadonlyArray<SettingsSubTabItem<AutomationSubTab>>;
}): AutomationSubTab =>
  subTabs.some((item) => item.value === activeSubTab)
    ? activeSubTab
    : 'reports';

export function AutomationTab({
  isReadOnly,
  isAiApplicationTarget,
}: AutomationTabProps) {
  const t = useTranslations('settings.automation');
  const tExecutions = useTranslations('designSystem.automation.executions');
  const tDynamicScan = useTranslations(
    'designSystem.automation.dynamicApplicationScan',
  );
  const form = useAutomationSettings();
  const dynamicScan = useDynamicApplicationScanSettings();
  const { selectedTarget } = form;
  const { isUpdating, persistApplicationSettings } =
    usePersistApplicationSettings({ applicationId: selectedTarget?.id });
  const { showDynamicApplicationScan } = dynamicScan;
  const showCrawler = !isAiApplicationTarget;
  const [activeSubTab, setActiveSubTab] = useState<AutomationSubTab>('reports');
  const subTabs = useMemo<ReadonlyArray<SettingsSubTabItem<AutomationSubTab>>>(
    () => [
      { value: 'reports', label: t('tabs.reports') },
      { value: 'executions', label: tExecutions('sectionTitle') },
      ...(showDynamicApplicationScan
        ? [
            {
              value: 'dynamicApplicationScan' as const,
              label: tDynamicScan('title'),
            },
          ]
        : []),
      ...(showCrawler
        ? [{ value: 'crawler' as const, label: t('tabs.crawler') }]
        : []),
    ],
    [showCrawler, showDynamicApplicationScan, t, tDynamicScan, tExecutions],
  );
  const visibleSubTab = resolveVisibleSubTab({ activeSubTab, subTabs });

  if (!selectedTarget) {
    return <SettingsEmptyState>{t('noTarget')}</SettingsEmptyState>;
  }

  return (
    <VStack gap={4} alignItems="stretch" w="full">
      {showDynamicApplicationScan ? null : (
        <RequestRateLimitCard
          applicationId={selectedTarget.id}
          isReadOnly={isReadOnly}
        />
      )}
      <SettingsSubTabs
        value={visibleSubTab}
        onValueChange={setActiveSubTab}
        items={subTabs}
      >
        <Tabs.Content value="reports">
          <VStack gap={4} alignItems="stretch" w="full">
            {isAiApplicationTarget ? (
              <NextPentestReportCard
                nextPentestReportDate={form.nextPentestReportDate}
                isReadOnly={isReadOnly}
                isSaving={isUpdating}
                onSave={(nextPentestReportDate) =>
                  persistApplicationSettings({
                    updates: { nextPentestReportDate },
                  })
                }
              />
            ) : (
              <ScheduledReportsConfigurationCard
                enabled={form.scheduledReports.enabled}
                rules={form.scheduledReports.rules}
                nextScheduledReportAt={
                  form.scheduledReports.nextScheduledReportAt
                }
                activeScheduledReportRuleCount={
                  form.scheduledReports.activeScheduledReportRuleCount
                }
                disabled={isReadOnly}
                isUpdating={isUpdating}
                onChange={(updates) =>
                  void persistApplicationSettings({
                    updates,
                    showSuccessToast: false,
                  })
                }
              />
            )}
          </VStack>
        </Tabs.Content>
        <Tabs.Content value="executions">
          <VStack gap={4} alignItems="stretch" w="full">
            <ExecutionsConfigurationCard
              smokeTestEnabled={form.smokeTestEnabled}
              supportsParallelExecution={form.supportsParallelExecution}
              limitExecutionTimeEnabled={form.limitExecutionTimeEnabled}
              automationSchedule={form.automationSchedule}
              disabled={isReadOnly}
              isUpdating={form.isSaving}
              onChange={form.handleExecutionsChange}
            />
            {isAiApplicationTarget ? null : (
              <LoginBehaviorCard
                applicationId={selectedTarget.id}
                isReadOnly={isReadOnly}
              />
            )}
            <AdaptiveScanningCard
              applicationId={selectedTarget.id}
              isReadOnly={isReadOnly}
            />
            <FullRescanControl
              applicationId={selectedTarget.id}
              isReadOnly={isReadOnly}
            />
            <FullRescanStatusCard applicationId={selectedTarget.id} />
          </VStack>
        </Tabs.Content>
        {showDynamicApplicationScan ? (
          <Tabs.Content value="dynamicApplicationScan">
            <VStack gap={4} alignItems="stretch" w="full">
              <DynamicApplicationScanConfigurationCard
                applicationId={selectedTarget.id}
                value={dynamicScan.draft}
                baseline={dynamicScan.baseline}
                activeRun={dynamicScan.automation?.activeRun}
                lastRun={dynamicScan.automation?.lastRun}
                disabled={dynamicScan.isSaving}
                isReadOnly={isReadOnly}
                hasUnsavedChanges={dynamicScan.hasUnsavedChanges}
                onChange={(next) => void dynamicScan.handleChange(next)}
              />
            </VStack>
          </Tabs.Content>
        ) : null}
        {showCrawler ? (
          <Tabs.Content value="crawler">
            <VStack gap={4} alignItems="stretch" w="full">
              <CrawlerConfigurationCard
                crawlerPeriodic={form.crawlerPeriodic}
                disabled={Boolean(selectedTarget.isBackendOnly) || isReadOnly}
                isUpdating={form.isSaving}
                onChange={form.setCrawlerPeriodic}
              />
              <DiscoveryContentTypesSection
                value={form.discoveryContentTypes}
                disabled={isReadOnly}
                onChange={form.setDiscoveryContentTypes}
              />
            </VStack>
          </Tabs.Content>
        ) : null}
      </SettingsSubTabs>
    </VStack>
  );
}
